import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import { getErrorString } from '../../utils';
import {
  fetchChats,
  fetchGethealthieMessagesApi,
  fetchLeadsApi,
  fetchMdiMessages,
  fetchMessageTemplates,
  fetchTelegramdMessagesApi,
  sendChatMessage,
  sendMdiChatMessage,
  sendTelegramdChatMessage,
} from '../../api/leads-api';
import { getQueryString } from '../../utils/filters-util';
import { setQuickLinks } from 'reducers/quick-links-slice';

const initialState = {
  templateLoading: false,
  hasMoreChat: true,
  templates: {},
  chats: [],
  messages: {},
  telegramdMessages: [],
  gethealthieMessages: [],
  activeColumns: [],
  leads: [],
  highLevelUsers: [],
  chatLoading: false,
  sendLoading: false,
  mdiMessageLoading: false,
  telegramdMessageLoading: false,
  gethealthieMessageLoading: false,
  loading: true,
  pending: false,
  pagination: {},
  isWaitingForProvider: false,
};

export const fetchChat = createAsyncThunk('leads/chats', async (param = {}) => {
  const paramsObj = {
    last_message_id: param?.last_message_id,
  };
  try {
    const res = await fetchChats({ leadId: param?.leadId, paramsStr: getQueryString(paramsObj) });
    return res;
  } catch (error) {}
});

export const fetchMessages = createAsyncThunk('leads/messages', async (param = {}) => {
  const paramsObj = {
    channel: param?.channel,
    mdi_page: param?.page,
  };
  try {
    const res = await fetchMdiMessages({ id: param?.id, paramsStr: getQueryString(paramsObj) });
    return res;
  } catch (error) {}
});

export const fetchTelegramdMessages = createAsyncThunk('telegramd/messages', async (param = {}) => {
  const paramsObj = {
    telegramd_page: param?.page,
  };
  try {
    const res = await fetchTelegramdMessagesApi({ id: param?.id, paramsStr: getQueryString(paramsObj) });
    return res;
  } catch (error) {}
});

export const fetchGethealthieMessages = createAsyncThunk('gethealthie/messages', async (param = {}) => {
  const paramsObj = {
    lead_id: param?.lead_id,
    gethealthie_page: param?.page,
  };
  try {
    const res = await fetchGethealthieMessagesApi({ id: param?.id, paramsStr: getQueryString(paramsObj) });
    return res;
  } catch (error) {}
});

export const sendChat = createAsyncThunk('leads/chats/send', async (param = {}) => {
  try {
    const res = await sendChatMessage(param);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const sendMdiChat = createAsyncThunk('leads/mdi/chats/send', async (param = {}) => {
  try {
    const res = await sendMdiChatMessage(param);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const sendTelegramdChat = createAsyncThunk('leads/telegram/chats/send', async (param = {}) => {
  try {
    const res = await sendTelegramdChatMessage(param);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadsListing = createAsyncThunk('leads/listing', async (updatedParamsObject = {}, { dispatch }) => {
  try {
    const hasLeadId = 'f[id][in]' in updatedParamsObject;
    const paramsObj = {
      ...(!hasLeadId ? { sort: 'patient_last_interaction_at desc' } : {}),
      ...updatedParamsObject,
    };
    const res = await fetchLeadsApi(paramsObj, '');
    if (res?.status) {
      dispatch(setQuickLinks(res?.user_filters));
    }
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchTemplates = createAsyncThunk('leads/templates', async () => {
  try {
    const res = await fetchMessageTemplates();
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

const LeadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    setLeadsLoading: (state, action) => {
      state.loading = action?.payload?.loading;
    },
    setLeadsActiveColumn: (state, action) => {
      state.activeColumns = action?.payload?.activeColumns;
    },
    setHighLevelUsers: (state, { payload }) => {
      state.highLevelUsers = payload;
    },
    setMdiMessages: (state, { payload }) => {
      state.messages = payload;
    },
    setTelegramdMessages: (state, { payload }) => {
      state.telegramdMessages = payload;
    },
    setGethealthieMessages: (state, { payload }) => {
      state.gethealthieMessages = payload;
    },
    setGethealthieLoading: (state, { payload }) => {
      state.gethealthieMessageLoading = payload;
    },
    setIsWaitingForProvider: (state, { payload }) => {
      state.isWaitingForProvider = payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchLeadsListing.pending, (state) => {
        state.pending = true;
      })
      .addCase(fetchChat.pending, (state, action) => {
        if (!action.meta?.arg?.last_message_id) state.chats = [];
        state.chatLoading = true;
      })
      .addCase(fetchMessages.pending, (state, action) => {
        if ((action.meta?.arg?.page || 1) === 1) state.messages = {};
        state.mdiMessageLoading = action.meta?.arg?.channel;
      })
      .addCase(fetchTelegramdMessages.pending, (state) => {
        state.telegramdMessages = [];
        state.telegramdMessageLoading = true;
      })
      .addCase(fetchGethealthieMessages.pending, (state, action) => {
        if ((action.meta?.arg?.page || 1) === 1) {
          state.gethealthieMessages = [];
        }
        state.gethealthieMessageLoading = true;
      })

      .addCase(sendChat.pending, (state) => {
        state.sendLoading = true;
      })
      .addCase(sendMdiChat.pending, (state) => {
        state.sendLoading = true;
      })
      .addCase(sendTelegramdChat.pending, (state) => {
        state.sendLoading = true;
      })
      .addCase(fetchTemplates.pending, (state) => {
        state.templateLoading = true;
      })
      .addCase(sendChat.fulfilled, (state) => {
        state.sendLoading = false;
      })
      .addCase(sendMdiChat.fulfilled, (state, action) => {
        state.messages?.data?.unshift(action?.payload?.data);
        state.sendLoading = false;
      })
      .addCase(sendTelegramdChat.fulfilled, (state, action) => {
        state.telegramdMessages = action?.payload?.data?.channel?.messages;
        state.sendLoading = false;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templateLoading = false;
        if (action?.payload?.status) {
          state.templates = action?.payload;
        }
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.mdiMessageLoading = false;
        if ((action.meta?.arg?.page || 1) > 1) {
          state.messages = {
            ...action?.payload?.data,
            data: [...(state.messages?.data || []), ...(action?.payload?.data?.data || [])],
          };
        } else {
          state.messages = action?.payload?.data;
        }
      })
      .addCase(fetchTelegramdMessages.fulfilled, (state, action) => {
        state.telegramdMessageLoading = false;
        state.telegramdMessages = action?.payload?.data?.channel?.messages;
      })
      .addCase(fetchGethealthieMessages.fulfilled, (state, { payload, meta }) => {
        state.gethealthieMessageLoading = false;
        state.gethealthieMessages = [
          ...(payload?.data?.notes || []).reverse(),
          ...((meta?.arg?.page || 1) > 1 ? state.gethealthieMessages || [] : []),
        ];
      })
      .addCase(sendChat.rejected, (state) => {
        state.sendLoading = false;
      })
      .addCase(sendMdiChat.rejected, (state) => {
        state.sendLoading = false;
      })
      .addCase(sendTelegramdChat.rejected, (state) => {
        state.sendLoading = false;
      })
      .addCase(fetchChat.fulfilled, (state, action) => {
        state.chatLoading = false;
        if (action?.payload && action?.payload?.status) {
          state.hasMoreChat = action?.payload?.data?.messages?.nextPage;
          state.chats = [
            ...(action?.payload?.data?.messages?.messages || []).reverse(),
            ...(action.meta?.arg?.last_message_id ? state.chats || [] : []),
          ];
        }
      })
      .addCase(fetchChat.rejected, (state) => {
        state.chatLoading = false;
      })
      .addCase(fetchMessages.rejected, (state) => {
        state.sendLoading = false;
      })
      .addCase(fetchTelegramdMessages.rejected, (state) => {
        state.telegramdMessageLoading = false;
      })
      .addCase(fetchGethealthieMessages.rejected, (state) => {
        state.gethealthieMessageLoading = false;
      })
      .addCase(fetchLeadsListing.fulfilled, (state, action) => {
        if (state.loading) {
          //means first time loading
          state.activeColumns = action?.payload?.active_columns || [];
        }
        state.leads = action?.payload?.data || [];
        state.loading = false;
        state.pending = false;
        state.pagination = action?.payload?.meta?.pagination || {};
      })
      .addCase(fetchLeadsListing.rejected, (state) => {
        state.loading = false;
        state.pending = false;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.templateLoading = false;
      });
  },
});

export const {
  setLeadsLoading,
  setLeadsActiveColumn,
  setHighLevelUsers,
  setMdiMessages,
  setTelegramdMessages,
  setGethealthieMessages,
  setGethealthieLoading,
  setIsWaitingForProvider,
} = LeadsSlice.actions;
export default LeadsSlice.reducer;
